import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import _union from 'lodash/union'
import _uniqBy from 'lodash/uniqBy'
import _isEmpty from 'lodash/isEmpty'

import {
  CloudSoftwareHistoryEnum,
  HomeCloudSoftwareHistoryType,
} from 'features/home/types'
import { HOME_CLOUD_SOFTWARE_HISTORY_REDUCER_NAME } from 'features/home/consts'
import {
  fetchHomeCloudSoftwareHistory,
  getSoftwareUpdateStatus,
  HomeCloudSoftwareHistoryParams,
} from 'features/home/api'

interface HomeSoftwareCloudHistoryResponse {
  next?: string
  updates: HomeCloudSoftwareHistoryType[]
}

interface HomeSoftwareCloudHistoryInitialState {
  cloudSoftwareHistory: HomeCloudSoftwareHistoryType[]
  next?: string
  allCloudSoftwareHistoryFetched: boolean
}
const initialState: HomeSoftwareCloudHistoryInitialState = {
  cloudSoftwareHistory: [],
  next: undefined,
  allCloudSoftwareHistoryFetched: false,
}

const homeCloudSoftwareHistorySlice = createSlice({
  name: HOME_CLOUD_SOFTWARE_HISTORY_REDUCER_NAME,
  initialState,
  reducers: {
    clear: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchCloudSoftwareHistoryThunk.fulfilled,
      (state, action) => {
        const { cloudSoftwareHistory, next } = state
        if (_isEmpty(action.payload)) {
          return {
            cloudSoftwareHistory: cloudSoftwareHistory,
            next: next,
            allCloudSoftwareHistoryFetched: true,
          }
        }
        const newCloudSoftwareHistory = _uniqBy(
          _union(action.payload?.updates, cloudSoftwareHistory),
          'updateId'
        )

        return {
          cloudSoftwareHistory: newCloudSoftwareHistory,
          next: action.payload?.next,
          allCloudSoftwareHistoryFetched: !action.payload?.next,
        }
      }
    )
    builder.addCase(
      getHomeSoftwareUpdateStatusThunk.fulfilled,
      (state, action) => {
        const newCloudSoftwareHistory: HomeCloudSoftwareHistoryType[] =
          state.cloudSoftwareHistory.map((e: HomeCloudSoftwareHistoryType) => {
            if (e.updateId === action.payload?.updateId) {
              if (
                action.payload.state === CloudSoftwareHistoryEnum.download &&
                !action.payload.message
              ) {
                action.payload.message = 'Preparing download'
              }
              return action.payload
            }
            return e
          })

        return {
          ...state,
          cloudSoftwareHistory: newCloudSoftwareHistory,
        }
      }
    )
  },
})

export const homeCloudSoftwareHistoryReducer =
  homeCloudSoftwareHistorySlice.reducer
export const homeCloudSoftwareHistoryActions =
  homeCloudSoftwareHistorySlice.actions

interface fetchCloudSoftwareHistoryProps {
  homeId: string
  partNumber: string
  params?: HomeCloudSoftwareHistoryParams
}

export const fetchCloudSoftwareHistoryThunk = createAsyncThunk<
  HomeSoftwareCloudHistoryResponse,
  fetchCloudSoftwareHistoryProps
>(
  'home/fetchCloudSoftwareHistory',
  async (props: fetchCloudSoftwareHistoryProps) => {
    try {
      const { homeId, partNumber, params } = props
      const cloudSoftwareHistoryData: HomeSoftwareCloudHistoryResponse = (
        await fetchHomeCloudSoftwareHistory(homeId, partNumber, params)
      ).data?.payload

      return cloudSoftwareHistoryData
    } catch (error) {
      console.log('Error fetching Cloud Software History', error)
      throw error
    }
  }
)

export const getHomeSoftwareUpdateStatusThunk = createAsyncThunk(
  'home/getHomeSoftwareUpdateStatus',
  async (props: { homeId: string; updateId: string }) => {
    try {
      const { homeId, updateId } = props
      const softwareUpdateStatus: HomeCloudSoftwareHistoryType = (
        await getSoftwareUpdateStatus(homeId, updateId)
      ).data.payload

      return softwareUpdateStatus
    } catch (error) {
      console.log('Error fetching Software Update Status', error)
    }
  }
)
